import React from "react";
import { isMobile } from "react-device-detect";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import { ReactComponent as ArrowDoubleLeftSVG } from "assets/img/ArrowDoubleLeft.svg";
import { ReactComponent as ArrowDoubleRightSVG } from "assets/img/ArrowDoubleRight.svg";
import { ReactComponent as ArrowLeftSVG } from "assets/img/ArrowLeft.svg";
import { ReactComponent as ArrowRightSVG } from "assets/img/ArrowRight.svg";

import breakpoints from "_constants/breakpoints";

const Container = styled.div`
  display: flex;
  width: fit-content;
  margin: auto;
  height: 66px;
  border: 1px solid #ecedf1;
  border-radius: 6px;
  margin-bottom: 75px;

  @media (max-width: ${breakpoints.mobileM}) {
    width: 100%;
    justify-content: space-around;
    border: none;
  }

  ${({ top }) =>
    top &&
    css`
      margin-top: 16px;
      margin-bottom: 8px;
    `}

  ${({ bottom }) =>
    bottom &&
    css`
      margin-top: 8px;
      margin-bottom: 16px;
    `}

      ${({ margin }) =>
    margin &&
    css`
      margin: ${margin};
    `}
`;
const Page = styled.a`
  user-select: none;
  min-width: 30px;
  height: 100%;
  text-decoration: none;
  text-align: center;
  line-height: 66px;
  margin-left: 7.5px;
  margin-right: 7.5px;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  ${({ active }) => (active ? "border-bottom: 3px solid #03589C; color: #03589C;" : "color: #7b848d;")}
  cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      min-width: 45px;

      @media (max-width: ${breakpoints.mobileM}) {
        min-width: 16px;
      }
    `}
  &:hover {
    color: ${({ active }) => (active ? "#03589C" : "#7b848d")};
    cursor: ${({ disabled }) => (disabled ? "default" : "pointer")};
    text-decoration: none;
  }
`;

const Enabled = styled(Page)`
  color: #f37217;
  font-weight: normal;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    color: #f37217;
    text-decoration: none;
  }
`;

const Disabled = styled(Page)`
  color: #a5aaaf;
  font-weight: normal;
  font-size: 14px;
  cursor: default;
  &:hover {
    color: #a5aaaf;
    text-decoration: none;
  }
`;

function Item({ disabled, children, ...props }) {
  return disabled ? <Disabled>{children}</Disabled> : <Enabled {...props}>{children}</Enabled>;
}

Item.propTypes = {
  disabled: PropTypes.bool,
  children: isMobile ? PropTypes.object : PropTypes.array,
};

function Pagination({ totalPages, currentPage, top, bottom, margin, onClick }) {
  if (!totalPages) return null;

  const pagination = () => {
    let items = [];

    if (!isMobile)
      for (let i = 1; i <= totalPages; i++) {
        if (i <= 3 || (i + 2 >= currentPage && i - 2 <= currentPage) || i >= totalPages - 2) {
          items.push(i);
        }
      }
    else
      for (let i = 1; i <= totalPages; i++) {
        if (i + 1 >= currentPage && i - 1 <= currentPage) {
          items.push(i);
        }
      }

    if (isMobile) return items;

    const firstBreak = items.findIndex((item, idx, arr) => arr[idx + 1] && item + 1 !== arr[idx + 1]);
    const secondBreak =
      firstBreak &&
      items.findIndex((item, idx, arr) => firstBreak !== idx && arr[idx + 1] && item + 1 !== arr[idx + 1]);

    if (firstBreak > -1) items.splice(secondBreak ? firstBreak + 1 : firstBreak, 0, "...");
    if (secondBreak > -1) items.splice(secondBreak + 2, 0, "...");

    return items;
  };

  return (
    <Container margin={margin} top={top} bottom={bottom}>
      {isMobile ? (
        <>
          <Item disabled={currentPage === 1} onClick={() => onClick(1)}>
            <ArrowDoubleLeftSVG fill={currentPage !== 1 ? "#F37217" : "#A5AAAF"} />
          </Item>
          {pagination().map((item, idx) => (
            <Page key={idx} active={currentPage === item} onClick={() => onClick(item)}>
              {item}
            </Page>
          ))}
          <Item disabled={currentPage === totalPages} onClick={() => onClick(totalPages)}>
            <ArrowDoubleRightSVG fill={currentPage !== totalPages ? "#F37217" : "#A5AAAF"} />
          </Item>
        </>
      ) : (
        <>
          <Item disabled={currentPage === 1} onClick={() => onClick(currentPage - 1)}>
            <ArrowLeftSVG fill={currentPage !== 1 ? "#F37217" : "#A5AAAF"} />
            {" prev"}
          </Item>
          {pagination().map((item, idx) =>
            item === "..." ? (
              <Page key={idx} disabled>
                {item}
              </Page>
            ) : (
              <Page key={idx} active={currentPage === item} onClick={() => onClick(item)}>
                {item}
              </Page>
            )
          )}
          <Item disabled={currentPage === totalPages} onClick={() => onClick(currentPage + 1)}>
            {"next "}
            <ArrowRightSVG fill={currentPage !== totalPages ? "#F37217" : "#A5AAAF"} />
          </Item>
        </>
      )}
    </Container>
  );
}

Pagination.propTypes = {
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  margin: PropTypes.string,
  top: PropTypes.bool,
  bottom: PropTypes.bool,
  onClick: PropTypes.func,
};

export default Pagination;
